import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowUpRight,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from "react-icons/fi";
import { publicAPI } from "../../api";
import { SectionHeader } from "../../components/common/CommonComponents";
import {
  getImageUrl,
  sanitizeHTML,
  truncate,
  formatDate,
  stripHtml,
} from "../../utils/helpers";
import {
  initScrollAnimations,
  initMagneticButtons,
  cleanupAnimations,
} from "../../utils/animations";
import "./Home.css";

/* ============================
   HERO IMAGE SLIDER
   ============================ */
const HeroSlider = ({ hero }) => {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const SLIDE_DURATION = 6000;

  const buildSlides = () => {
    let meta = hero?.metadata;
    if (typeof meta === "string") {
      try {
        meta = JSON.parse(meta);
      } catch {
        meta = null;
      }
    }
    if (meta?.slides?.length > 0) {
      return meta.slides
        .filter((s) => s.title?.trim())
        .map((s) => ({
          title: s.title || "",
          subtitle: s.subtitle || "",
          label: s.label || "",
          image: s.image ? getImageUrl(s.image) : null,
        }));
    }
    return [
      {
        title: hero?.title || "First College in Belagavi with Education 4.0",
        subtitle:
          hero?.subtitle ||
          "PeopleTree Education Society — Building brand YOU with corporate success, market-aligned courses, and quality placements since 2007.",
        label: "Since 2007",
        image: hero?.image_url ? getImageUrl(hero.image_url) : null,
      },
      {
        title: "BBA in Tech | BCA Corporate | PU Commerce",
        subtitle:
          "Industry-aligned programs designed under Education 4.0 framework for the modern world.",
        label: "Our Programs",
        image: null,
      },
      {
        title: "Shape Your Future at PTeS",
        subtitle:
          "19+ years of excellence in creating budding entrepreneurs and industry-ready graduates.",
        label: "Belagavi",
        image: null,
      },
    ];
  };

  const slides = buildSlides();

  const goTo = useCallback((idx) => {
    setActive(idx);
    setProgress(0);
  }, []);

  const next = useCallback(
    () => goTo((active + 1) % slides.length),
    [active, slides.length, goTo],
  );
  const prev = useCallback(
    () => goTo((active - 1 + slides.length) % slides.length),
    [active, slides.length, goTo],
  );

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, SLIDE_DURATION);

    const progressInterval = setInterval(() => {
      setProgress((p) => {
        const next = p + 50 / SLIDE_DURATION;
        return next >= 1 ? 0 : next;
      });
    }, 50);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(progressInterval);
    };
  }, [slides.length]);

  useEffect(() => {
    const gsap = window.gsap;
    if (!gsap) return;
    const tl = gsap.timeline();
    tl.fromTo(
      ".hero__slide-label",
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
    );
    tl.fromTo(
      ".hero__slide-title",
      { y: 60, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7, ease: "power3.out" },
      "-=0.3",
    );
    tl.fromTo(
      ".hero__slide-desc",
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: "power3.out" },
      "-=0.3",
    );
    tl.fromTo(
      ".hero__slide-actions",
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
      "-=0.2",
    );
    setProgress(0);
  }, [active]);

  return (
    <section className="hero">
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`hero__bg-slide ${i === active ? "hero__bg-slide--active" : ""}`}
        >
          <div className="hero__bg-overlay" />
          {slide.image && !slide.image.includes("placeholder") && (
            <img loading={i === 0 ? "eager" : "lazy"} decoding="async" src={slide.image} alt="" className="hero__bg-img" />
          )}
        </div>
      ))}

      <div className="hero__grain" />

      <div className="container hero__content">
        <div className="hero__slide-content">
          <span className="hero__slide-label">{slides[active].label}</span>
          <h1 className="hero__slide-title">{slides[active].title}</h1>
          <p className="hero__slide-desc">{slides[active].subtitle}</p>
          <div className="hero__slide-actions">
            <Link
              to="/courses"
              className="btn btn--secondary btn--lg btn-magnetic"
            >
              Explore Courses <FiArrowRight />
            </Link>
            <Link to="/contact" className="btn btn--ghost btn--lg btn-magnetic">
              Apply Now <FiArrowUpRight />
            </Link>
          </div>
        </div>

        <div className="hero__controls">
          <button className="hero__arrow" onClick={prev}>
            <FiChevronLeft />
          </button>
          <div className="hero__progress-wrap">
            <span className="hero__counter">
              {String(active + 1).padStart(2, "0")}
            </span>
            <div className="hero__progress-bar">
              <div
                className="hero__progress-fill"
                style={{ transform: `scaleX(${progress})` }}
              />
            </div>
            <span className="hero__counter hero__counter--total">
              {String(slides.length).padStart(2, "0")}
            </span>
          </div>
          <button className="hero__arrow" onClick={next}>
            <FiChevronRight />
          </button>
        </div>
      </div>

      <div className="hero__scroll-indicator">
        <div className="hero__scroll-line" />
        <span>Scroll</span>
      </div>
    </section>
  );
};

/* ============================
   MARQUEE COMPONENT
   ============================ */
const Marquee = ({ items, speed = 30 }) => {
  const doubled = [...items, ...items];
  return (
    <div className="marquee anim-marquee">
      <div
        className="marquee__inner anim-marquee__inner"
        style={{ animationDuration: `${speed}s` }}
      >
        {doubled.map((item, i) => (
          <div key={i} className="marquee__item">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================
   WORKSHOPS IMAGE CAROUSEL
   ============================ */
const WorkshopsCarousel = ({ images }) => {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const touchStart = useRef(null);
  const count = images.length;

  useEffect(() => {
    const timer = setInterval(() => setActive((p) => (p + 1) % count), 4000);
    return () => clearInterval(timer);
  }, [count]);

  const onDragStart = (e) => {
    touchStart.current =
      e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
  };
  const onDragEnd = (e) => {
    if (touchStart.current === null) return;
    const end =
      e.type === "touchend" ? e.changedTouches[0].clientX : e.clientX;
    const diff = touchStart.current - end;
    if (Math.abs(diff) > 40)
      setActive((p) => (diff > 0 ? (p + 1) % count : (p - 1 + count) % count));
    touchStart.current = null;
  };

  const CARD_HEIGHT = 280;
  const ACTIVE_W = 380;
  const INACTIVE_W = 260;
  const GAP = 20; // 1.25rem

  // Left-anchored: active item always starts at a fixed small offset from the left.
  // Items scroll left as active increases — no empty space at the start.
  const offset = active * (INACTIVE_W + GAP);

  return (
    <div style={{ position: "relative", userSelect: "none", paddingBottom: "3rem" }}>
      <div
        onMouseDown={onDragStart}
        onMouseUp={onDragEnd}
        onTouchStart={onDragStart}
        onTouchEnd={onDragEnd}
        style={{
          overflow: "hidden",
          cursor: "grab",
          height: CARD_HEIGHT + 32,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: `${GAP}px`,
            transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
            transform: `translateX(calc(-${offset}px + 1.5rem))`,
            alignItems: "center",
          }}
        >
          {images.map((img, i) => {
            const isActive = i === active;
            return (
              <div
                key={i}
                onClick={() => isActive ? setLightbox(img) : setActive(i)}
                style={{
                  flexShrink: 0,
                  width: isActive ? ACTIVE_W : INACTIVE_W,
                  height: CARD_HEIGHT,
                  borderRadius: "14px",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.45s ease, box-shadow 0.45s ease, border 0.45s ease",
                  opacity: isActive ? 1 : 0.55,
                  boxShadow: isActive
                    ? "0 20px 60px rgba(0,0,0,0.22)"
                    : "0 4px 16px rgba(0,0,0,0.10)",
                  border: isActive
                    ? "3px solid var(--clr-secondary)"
                    : "2px solid transparent",
                  position: "relative",
                }}
              >
                <img
                  src={getImageUrl(img.url || img)}
                  alt={img.caption || `Workshop ${i + 1}`}
                  loading={i < 3 ? "eager" : "lazy"}
                  decoding="async"
                  style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
                  draggable={false}
                />
                {isActive && img.caption && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
                      padding: "1.5rem 1rem 0.75rem",
                      color: "#fff",
                      fontSize: "0.82rem",
                      fontWeight: 500,
                    }}
                  >
                    {img.caption}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <button
        onClick={() => setActive((p) => (p - 1 + count) % count)}
        style={{
          position: "absolute",
          left: 24,
          top: `${(CARD_HEIGHT + 32) / 2}px`,
          transform: "translateY(-50%)",
          background: "#fff",
          border: "none",
          borderRadius: "50%",
          width: 52,
          height: 52,
          boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.6rem",
          zIndex: 10,
          color: "var(--clr-primary)",
          fontWeight: 300,
        }}
        aria-label="Previous"
      >
        ‹
      </button>
      <button
        onClick={() => setActive((p) => (p + 1) % count)}
        style={{
          position: "absolute",
          right: 24,
          top: `${(CARD_HEIGHT + 32) / 2}px`,
          transform: "translateY(-50%)",
          background: "#fff",
          border: "none",
          borderRadius: "50%",
          width: 52,
          height: 52,
          boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.6rem",
          zIndex: 10,
          color: "var(--clr-primary)",
          fontWeight: 300,
        }}
        aria-label="Next"
      >
        ›
      </button>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            style={{
              width: i === active ? 28 : 10,
              height: 10,
              borderRadius: 5,
              background:
                i === active ? "var(--clr-secondary)" : "var(--clr-border-light)",
              border: "none",
              cursor: "pointer",
              transition: "all 0.3s ease",
              padding: 0,
              flexShrink: 0,
            }}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: "fixed",
            top: 0, right: 0, bottom: 0, left: 0,
            background: "rgba(0,0,0,0.88)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            cursor: "zoom-out",
          }}
        >
          <img
            src={getImageUrl(lightbox.url || lightbox)}
            alt={lightbox.caption || ""}
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              borderRadius: 12,
              objectFit: "contain",
            }}
          />
        </div>
      )}
    </div>
  );
};

/* ============================
   HOME GALLERY CAROUSEL
   ============================ */
const HomeGalleryCarousel = ({ galleries }) => {
  const [active, setActive] = useState(0);
  const count = galleries.length;

  const prev = () => setActive((p) => (p - 1 + count) % count);
  const next = useCallback(() => setActive((p) => (p + 1) % count), [count]);

  useEffect(() => {
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [next]);

  return (
    <section className="section section--alt">
      <div className="container">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--sp-2xl)", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <span className="label-tag anim-fade-up">Visual Stories</span>
            <h2 className="anim-word-reveal" style={{ marginTop: "var(--sp-sm)" }}>
              Campus Gallery
            </h2>
          </div>
          <Link to="/life/gallery" className="btn btn--outline btn-magnetic">
            View All <FiArrowRight />
          </Link>
        </div>

        {/* Carousel wrapper */}
        <div style={{ position: "relative" }}>
          {/* Main image */}
          <div
            style={{
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              height: "clamp(260px, 45vw, 500px)",
              background: "#111",
              position: "relative",
            }}
          >
            {galleries.map((g, i) => (
              <div
                key={g.id || i}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: i === active ? 1 : 0,
                  transition: "opacity 0.5s ease",
                  pointerEvents: i === active ? "auto" : "none",
                }}
              >
                {g.cover_image ? (
                  <img loading="lazy" decoding="async"
                    src={getImageUrl(g.cover_image)}
                    alt={g.title || `Gallery ${i + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <div style={{ width: "100%", height: "100%", background: "var(--clr-bg-alt)" }} />
                )}
                {/* Caption */}
                {g.title && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "2rem 1.5rem 1.25rem",
                      background: "linear-gradient(transparent, rgba(0,0,0,0.55))",
                    }}
                  >
                    <p style={{ color: "#fff", fontWeight: 600, fontSize: "var(--fs-md)", margin: 0 }}>
                      {g.title}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Prev / Next arrows */}
            <button
              onClick={prev}
              aria-label="Previous"
              style={{
                position: "absolute",
                left: 14,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 5,
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.9)",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                color: "var(--clr-primary)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#fff")}
              onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.9)")}
            >
              <FiChevronLeft />
            </button>
            <button
              onClick={next}
              aria-label="Next"
              style={{
                position: "absolute",
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 5,
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.9)",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                color: "var(--clr-primary)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#fff")}
              onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.9)")}
            >
              <FiChevronRight />
            </button>
          </div>

          {/* Dot indicators */}
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "1.25rem" }}>
            {galleries.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                style={{
                  width: i === active ? 28 : 10,
                  height: 10,
                  borderRadius: 5,
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.3s ease",
                  background: i === active ? "var(--clr-primary)" : "var(--clr-border-light)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================
   MAIN HOME COMPONENT
   ============================ */
const FOUNDING_YEAR = 2007;

const Home = () => {
  const [sections, setSections] = useState([]);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [news, setNews] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [mouModal, setMouModal] = useState(null);
  const yearsOfExcellence = new Date().getFullYear() - FOUNDING_YEAR;

  const prevTestimonial = useCallback(
    () => setActiveTestimonial((p) => (p === 0 ? testimonials.length - 1 : p - 1)),
    [testimonials.length],
  );
  const nextTestimonial = useCallback(
    () => setActiveTestimonial((p) => (p + 1) % testimonials.length),
    [testimonials.length],
  );

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const interval = setInterval(nextTestimonial, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, nextTestimonial]);

  useEffect(() => {
    const load = async () => {
      try {
        const [secR, aboutR, courR, testR, newsR, recR, galR, setR] =
          await Promise.all([
            publicAPI
              .getPageSections("home")
              .catch(() => ({ data: { data: [] } })),
            publicAPI
              .getPageSections("about")
              .catch(() => ({ data: { data: [] } })),
            publicAPI
              .getCourses({ is_active: true })
              .catch(() => ({ data: { data: [] } })),
            publicAPI
              .getTestimonials({ is_active: true, limit: 6 })
              .catch(() => ({ data: { data: [] } })),
            publicAPI
              .getPublishedNews({ limit: 3 })
              .catch(() => ({ data: { data: [] } })),
            publicAPI
              .getRecruiters({ is_active: true })
              .catch(() => ({ data: { data: [] } })),
            publicAPI
              .getGalleries({ is_active: true, limit: 8 })
              .catch(() => ({ data: { data: [] } })),
            publicAPI.getSettings().catch(() => ({ data: { data: {} } })),
          ]);
        setSections([...(secR.data.data || []), ...(aboutR.data.data || [])]);
        setCourses(courR.data.data || []);
        setTestimonials(testR.data.data || []);
        setNews(newsR.data.data || []);
        setRecruiters(recR.data.data || []);
        setGalleries((galR.data.data || []).filter((g) => g.cover_image));
        setSettings(setR.data.data || {});
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      initScrollAnimations();
      initMagneticButtons();
    }, 300);
    return () => {
      clearTimeout(timer);
      cleanupAnimations();
    };
  }, [courses, testimonials, news, recruiters]);

  const getSection = (key) => sections.find((s) => s.section_key === key);
  const hero = getSection("hero_banner");
  const overview = getSection("overview");
  const intro = getSection("intro");
  const workshopsSection = getSection("workshops");

  let workshopImages = [];
  if (workshopsSection?.metadata) {
    let meta = workshopsSection.metadata;
    if (typeof meta === "string") {
      try { meta = JSON.parse(meta); } catch { meta = {}; }
    }
    workshopImages = Array.isArray(meta.images) ? meta.images : [];
  }

  let stats = settings.highlight_stats?.value || settings.highlight_stats || [];
  if (typeof stats === "string") {
    try {
      stats = JSON.parse(stats);
    } catch {
      stats = [];
    }
  }
  if (!Array.isArray(stats)) stats = [];

  const defaultStats = [
    { label: "Years of Excellence", value: String(yearsOfExcellence), suffix: "+" },
    { label: "Expert Faculty", value: "25", suffix: "+" },
    { label: "Programs", value: "4", suffix: "" },
    { label: "Placement Rate", value: "95", suffix: "%" },
  ];
  const displayStats = stats.length > 0 ? stats : defaultStats;

  return (
    <div className="home-premium">
      {/* ===== HERO SLIDER ===== */}
      <HeroSlider hero={hero} />

      {/* ===== ABOUT SECTION (moved directly after hero) ===== */}
      <section className="about-section section">
        <div className="container about-section__grid">
          <div className="about-section__left anim-slide-left">
            <div className="about-section__img-wrap">
              {intro?.image_url || overview?.image_url ? (
                <img
                  src={getImageUrl(intro?.image_url || overview?.image_url)}
                  alt="About PeopleTree"
                  className="about-section__img"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="about-section__img-placeholder" />
              )}
              <div className="about-section__experience">
                <span className="about-section__exp-number">{yearsOfExcellence}+</span>
                <span className="about-section__exp-label">
                  Years of Excellence
                </span>
              </div>
            </div>
          </div>
          <div className="about-section__right">
            <span className="label-tag anim-fade-up">About Us</span>
            <h2 className="about-section__title anim-fade-up">
              {intro?.title
                ? intro.title
                    .replace(/<\/?(p|div|br|h[1-6]|li|td|th)[^>]*>/gi, " ")
                    .replace(/<[^>]*>/g, "")
                    .replace(/&nbsp;/g, " ")
                    .replace(/&amp;/g, "&")
                    .replace(/\s+/g, " ")
                    .trim()
                : "Welcome to PeopleTree Education Society"}
            </h2>
            {intro?.content ? (
              <div
                className="about-section__text anim-fade-up"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(intro.content) }}
              />
            ) : (
              <>
                <p className="about-section__text anim-fade-up">
                  PeopleTree Education Society – A Registered Charitable Trust to
                  serve in the field of higher education was established in the year
                  2007. We make education an active and participatory learning,
                  contributing towards students' intellectual, practical and ethical
                  preparation for life in a complex, highly competitive and fast
                  changing world.
                </p>
              </>
            )}
            <div className="about-section__features anim-fade-up">
              <div className="about-section__feature">
                <div className="about-section__feature-dot" />
                <span>Education 4.0 Framework</span>
              </div>
              <div className="about-section__feature">
                <div className="about-section__feature-dot" />
                <span>Industry-Aligned Curriculum</span>
              </div>
              <div className="about-section__feature">
                <div className="about-section__feature-dot" />
                <span>Quality Placements</span>
              </div>
            </div>
            <Link
              to="/about"
              className="btn btn--primary btn--lg btn-magnetic anim-fade-up"
            >
              Explore More <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WORKSHOPS & EVENTS CAROUSEL ===== */}
      {workshopImages.length > 0 && (
        <section className="section" style={{ paddingBottom: 0 }}>
          <div className="container">
            <div style={{ textAlign: "center", marginBottom: "var(--sp-xl)" }}>
              <span className="label-tag anim-fade-up">
                {workshopsSection?.title || "Events & Workshops"}
              </span>
              <h2 className="anim-word-reveal" style={{ marginTop: "var(--sp-sm)" }}>
                {workshopsSection?.subtitle || "Learning Beyond Classrooms"}
              </h2>
            </div>
          </div>
          <WorkshopsCarousel images={workshopImages} />
        </section>
      )}

      {/* ===== PLACEMENT PARTNERS ===== */}
      {recruiters.length > 0 && (
        <section className="stats-section">
          <div className="container">
            <div style={{ textAlign: "center", marginBottom: "var(--sp-lg)" }}>
              <span className="label-tag anim-fade-up">Placement Partners</span>
              <h2 className="anim-word-reveal">Where Our Students Work</h2>
            </div>
            <div className="recruiters-grid">
              {recruiters.map((r) => (
                <div key={r.id} className="recruiter-logo recruiter-logo--flippable">
                  {/* Default: logo */}
                  <div className="recruiter-logo__front">
                    {r.logo_url ? (
                      <img src={getImageUrl(r.logo_url)} alt={r.name} loading="lazy" decoding="async" />
                    ) : (
                      <span>{r.name}</span>
                    )}
                  </div>
                  {/* Hover overlay: MOU */}
                  <div
                    className="recruiter-logo__back"
                    onClick={r.mou_url ? (e) => { e.stopPropagation(); setMouModal(r); } : undefined}
                    style={r.mou_url ? { cursor: "zoom-in" } : undefined}
                  >
                    {r.mou_url ? (
                      <>
                        <img loading="lazy" decoding="async"
                          src={getImageUrl(r.mou_url)}
                          alt={`${r.name} MOU`}
                          className="recruiter-logo__mou-img"
                        />
                        <span className="recruiter-logo__mou-label">MOU — {r.name} &nbsp;·&nbsp; Click to view</span>
                      </>
                    ) : (
                      <>
                        <span className="recruiter-logo__partner-name">{r.name}</span>
                        <span className="recruiter-logo__mou-label">Placement Partner</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== HORIZONTAL DIVIDER MARQUEE ===== */}
      <div className="divider-marquee">
        <Marquee
          items={[
            "BBA in Tech",
            "✦",
            "BCA Corporate",
            "✦",
            "PU Commerce",
            "✦",
            "Education 4.0",
            "✦",
            "Belagavi",
            "✦",
          ]}
          speed={25}
        />
      </div>

      {/* ===== COURSES ===== */}
      <section className="courses-section section">
        <div className="container">
          <div className="courses-section__header">
            <div>
              <span className="label-tag anim-fade-up">Programs</span>
              <h2 className="courses-section__title anim-word-reveal">
                Our Courses
              </h2>
            </div>
            <p className="courses-section__desc anim-fade-up">
              Discover our range of undergraduate and professional programs
              designed for the modern world.
            </p>
          </div>

          <div className="courses-grid anim-stagger-parent">
            {(courses.length > 0
              ? courses
              : [
                  {
                    id: 1,
                    slug: "bba",
                    name: "BBA in Tech",
                    overview:
                      "Bachelor of Business Administration with Education 4.0, blending management with technology.",
                  },
                  {
                    id: 2,
                    slug: "bca",
                    name: "BCA – Corporate",
                    overview:
                      "Bachelor of Computer Applications with corporate-ready IT skills and industry certifications.",
                  },
                  {
                    id: 4,
                    slug: "pu-commerce",
                    name: "PU Commerce",
                    overview:
                      "Pre-University Commerce preparing students for higher education and professional courses.",
                  },
                ]
            )
              .slice(0, 4)
              .map((c, i) => (
                <Link
                  to={`/courses/${c.slug}`}
                  key={c.id}
                  className="course-block anim-stagger-child"
                >
                  <div className="course-block__number">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="course-block__content">
                    <h3 className="course-block__title">{c.name}</h3>
                    <p className="course-block__text">
                      {truncate(stripHtml(c.overview), 120)}
                    </p>
                    <span className="course-block__link">
                      Learn More <FiArrowRight />
                    </span>
                  </div>
                  <div className="course-block__arrow">
                    <FiArrowUpRight />
                  </div>
                  <div className="course-block__bg-hover" />
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS — SLIDER ===== */}
      {testimonials.length > 0 && (
        <section className="testimonial-section section">
          <div className="container">
            <div className="ts-header anim-fade-up">
              <span className="label-tag label-tag--light">Testimonials</span>
              <h2 className="testimonial-section__title">What People Say</h2>
            </div>

            <div className="ts-wrap">
              {/* Prev arrow */}
              <button
                className="ts-arrow ts-arrow--prev"
                onClick={prevTestimonial}
                aria-label="Previous testimonial"
              >
                <FiChevronLeft />
              </button>

              {/* Sliding track */}
              <div className="ts-track">
                <div
                  className="ts-inner"
                  style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                >
                  {testimonials.map((t, i) => (
                    <div key={t.id || i} className="ts-card">
                      {/* Left — photo + name + batch */}
                      <div className="ts-card__left">
                        <div className="ts-card__avatar">
                          {t.photo_url ? (
                            <img src={getImageUrl(t.photo_url)} alt={t.name} loading="lazy" decoding="async" />
                          ) : (
                            <span>{t.name?.charAt(0)}</span>
                          )}
                        </div>
                        <div className="ts-card__person">
                          <strong>{t.name}</strong>
                          {t.designation && <span>{t.designation}</span>}
                        </div>
                      </div>
                      {/* Right — quote */}
                      <div className="ts-card__right">
                        <span className="ts-card__big-quote">"</span>
                        <p className="ts-card__text">{t.quote}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next arrow */}
              <button
                className="ts-arrow ts-arrow--next"
                onClick={nextTestimonial}
                aria-label="Next testimonial"
              >
                <FiChevronRight />
              </button>
            </div>

            {/* Dots */}
            <div className="ts-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`ts-dot ${i === activeTestimonial ? "ts-dot--active" : ""}`}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== NEWS ===== */}
      {news.length > 0 && (
        <section className="news-section section">
          <div className="container">
            <div className="news-section__header">
              <div>
                <span className="label-tag anim-fade-up">Latest</span>
                <h2 className="anim-word-reveal">News & Updates</h2>
              </div>
              <Link
                to="/life/news"
                className="btn btn--outline btn-magnetic anim-fade-up"
              >
                View All <FiArrowRight />
              </Link>
            </div>
            <div className="news-grid anim-stagger-parent">
              {news.map((n) => (
                <Link
                  to={`/life/news/${n.slug}`}
                  key={n.id}
                  className="news-block anim-stagger-child"
                >
                  <div className="news-block__img-wrap">
                    {n.featured_image && (
                      <img loading="lazy" decoding="async"
                        src={getImageUrl(n.featured_image)}
                        alt={n.title}
                        className="news-block__img"
                      />
                    )}
                    <span className="news-block__category">{n.category}</span>
                  </div>
                  <div className="news-block__body">
                    <time className="news-block__date">
                      <FiCalendar /> {formatDate(n.published_at)}
                    </time>
                    <h4 className="news-block__title">{n.title}</h4>
                    <span className="news-block__read-more">
                      Read More <FiArrowRight />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== GALLERY CAROUSEL ===== */}
      {galleries.length > 0 && <HomeGalleryCarousel galleries={galleries} />}

      {/* ===== MOU POPUP MODAL ===== */}
      {mouModal && (
        <div
          className="modal-overlay"
          onClick={() => setMouModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`MOU — ${mouModal.name}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "var(--radius-lg)",
              maxWidth: "min(90vw, 800px)",
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              position: "relative",
            }}
          >
            {/* Header */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1rem 1.25rem",
              borderBottom: "1px solid var(--clr-border-light)",
            }}>
              <div>
                <span style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--clr-secondary)" }}>
                  Memorandum of Understanding
                </span>
                <h3 style={{ margin: 0, fontSize: "var(--fs-md)", color: "var(--clr-primary-dark)" }}>
                  {mouModal.name}
                </h3>
              </div>
              <button
                onClick={() => setMouModal(null)}
                aria-label="Close"
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--clr-text-muted)", fontSize: "1.4rem",
                  display: "flex", alignItems: "center", padding: "0.25rem",
                }}
              >
                <FiX />
              </button>
            </div>
            {/* MOU Image */}
            <div style={{ padding: "1rem" }}>
              <img
                src={getImageUrl(mouModal.mou_url)}
                alt={`MOU — ${mouModal.name}`}
                style={{ width: "100%", height: "auto", borderRadius: "var(--radius-md)", display: "block" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
